import { type CustomFieldDef, customFieldsApi } from '@/api/queries/workspace';
import { isTenantAdmin, useTenantStore } from '@/auth/tenantStore';
import { IconButton, Table, type TableColumn, Tag } from '@/components/ui';

const TYPE_LABELS: Record<string, string> = {
  list: 'List',
  text: 'Text',
  textarea: 'Long text',
  email: 'Email',
  tel: 'Phone',
  number: 'Number',
  date: 'Date',
  select: 'Select',
  url: 'URL',
};

export function CustomFieldsPage() {
  const { data: fields = [], isPending } = customFieldsApi.useList();
  const remove = customFieldsApi.useDelete();
  const canManage = useTenantStore(isTenantAdmin);

  const columns: TableColumn<CustomFieldDef>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (f) => <span style={{ fontWeight: 500 }}>{f.name}</span>,
    },
    {
      id: 'key',
      header: 'Key',
      width: 200,
      cell: (f) =>
        f.key ? (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)' }}>
            {f.key}
          </span>
        ) : (
          <span style={{ color: 'var(--text-4)' }}>—</span>
        ),
    },
    {
      id: 'type',
      header: 'Type',
      width: 120,
      cell: (f) => (
        <Tag tone={f.type === 'list' ? 'brand' : 'default'}>{TYPE_LABELS[f.type] ?? f.type}</Tag>
      ),
    },
    {
      id: 'section',
      header: 'Section',
      width: 130,
      cell: (f) => f.section || <span style={{ color: 'var(--text-4)' }}>—</span>,
    },
    {
      id: 'source',
      header: 'Source',
      width: 140,
      cell: (f) => f.source || <span style={{ color: 'var(--text-4)' }}>—</span>,
    },
    {
      id: 'created',
      header: 'Created',
      width: 130,
      cell: (f) => formatDate(f.createdAt),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <header className="page-header">
        <div>
          <h1 className="page-title">Custom fields</h1>
          <div className="page-subtitle">
            Custom contact fields of this tenant. Feeders such as the MLSK web create list fields on
            their first import; the values appear in the contact detail. Deleting a definition keeps
            the values on contacts.
          </div>
        </div>
      </header>

      <Table<CustomFieldDef>
        columns={columns}
        rows={fields}
        rowKey={(f) => f.id}
        loading={isPending}
        emptyMessage="No custom fields yet — they are created by the first import that sends one."
        rowActions={(f) =>
          canManage ? (
            <IconButton
              icon="trash"
              iconSize={13}
              size="sm"
              label="Delete custom field"
              className="row-action danger"
              onClick={() => {
                if (
                  confirm(
                    `Delete custom field "${f.name}"? Values already stored on contacts are kept.`,
                  )
                ) {
                  remove.mutate(f.id);
                }
              }}
            />
          ) : null
        }
      />
    </div>
  );
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
